<template>
  <div class="msv">
    <a-dropdown :trigger="['click']" placement="bottomLeft" :disabled="!loaded">
      <a-button class="msv__trigger mf-btn">
        <a-icon type="star" :theme="activeView ? 'filled' : 'outlined'" />
        <span class="msv__label">{{ triggerLabel }}</span>
        <span v-if="isModified" class="msv__modified-dot" :title="$t('monitor.views.modified')"></span>
        <a-icon type="down" class="msv__caret" />
      </a-button>

      <a-menu slot="overlay" class="msv__menu">
        <a-menu-item v-if="!views.length" disabled class="msv__empty">
          {{ $t('monitor.views.empty') }}
        </a-menu-item>
        <a-menu-item
          v-for="view in views"
          :key="idOf(view)"
          :class="{ 'msv__item--active': activeView && idOf(activeView) === idOf(view) }"
          @click="loadView(view)"
        >
          <div class="msv__item">
            <a-icon type="star" :theme="activeView && idOf(activeView) === idOf(view) ? 'filled' : 'outlined'" class="msv__item-icon" />
            <span class="msv__item-name">{{ view.name }}</span>
            <a-icon
              type="edit"
              class="msv__item-action"
              :title="$t('monitor.views.edit')"
              @click.stop="openEditModal(view)"
            />
            <a-icon
              type="delete"
              class="msv__item-action msv__item-action--danger"
              :title="$t('monitor.views.delete')"
              @click.stop="confirmDelete(view)"
            />
          </div>
        </a-menu-item>

        <a-menu-divider />

        <a-menu-item key="__save" @click="openSaveModal">
          <a-icon type="plus" /> {{ $t('monitor.views.save') }}
        </a-menu-item>
        <a-menu-item
          v-if="activeView"
          key="__update"
          :disabled="!isModified"
          @click="updateActive"
        >
          <a-icon type="save" /> {{ $t('monitor.views.update') }}
        </a-menu-item>
      </a-menu>
    </a-dropdown>

    <a-modal
      v-model="nameModalVisible"
      :title="$t('monitor.views.namePrompt')"
      :ok-text="$t('monitor.views.save')"
      :confirm-loading="saving"
      :ok-button-props="{ props: { disabled: !pendingName.trim() } }"
      @ok="commitSaveAsNew"
      @cancel="nameModalVisible = false"
    >
      <a-input
        v-model="pendingName"
        :placeholder="$t('monitor.views.namePlaceholder')"
        :max-length="120"
        autofocus
        @pressEnter="commitSaveAsNew"
      />
    </a-modal>

    <a-modal
      v-model="editModalVisible"
      :title="$t('monitor.views.editTitle')"
      :ok-text="$t('monitor.views.saveChanges')"
      :confirm-loading="saving"
      :ok-button-props="{ props: { disabled: !canCommitEdit } }"
      @ok="commitEdit"
      @cancel="editModalVisible = false"
    >
      <div class="msv__edit-form">
        <label class="msv__edit-label">{{ $t('monitor.views.nameField') }}</label>
        <a-input
          v-model="editName"
          :placeholder="$t('monitor.views.namePlaceholder')"
          :max-length="120"
          autofocus
          @pressEnter="commitEdit"
        />
        <a-checkbox
          v-if="editFiltersDiffer"
          v-model="editReplaceFilters"
          class="msv__edit-replace"
        >
          {{ $t('monitor.views.replaceFilters') }}
        </a-checkbox>
        <p v-else class="msv__edit-hint">
          {{ $t('monitor.views.filtersIdentical') }}
        </p>
      </div>
    </a-modal>
  </div>
</template>

<script>
// Increment when the shape of `filters` changes in an incompatible way.
// Migrations live in `migrateFilters` below.
const CURRENT_VERSION = 1;

// In-memory migrations. Each entry takes a v(n) payload and returns v(n+1).
// Add a new entry whenever CURRENT_VERSION is bumped so older saved views
// still load cleanly on newer clients.
const MIGRATIONS = {
  // 1: filters => ({ ...filters, newKey: defaultValue }),
};

function migrateFilters(payload, fromVersion) {
  let current = payload;
  let v = fromVersion || 1;
  while (v < CURRENT_VERSION && typeof MIGRATIONS[v] === 'function') {
    current = MIGRATIONS[v](current);
    v += 1;
  }
  return current;
}

// Feathers responses can surface the record id as `id`, `_id`, or both
// depending on how the service serializes. Pick whichever is present.
function idOf(view) {
  if (!view) return null;
  return view.id || view._id || null;
}

export default {
  name: 'MonitorSavedViews',
  props: {
    currentFilters: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    loaded: false,
    loading: false,
    saving: false,
    activeViewId: null,
    activeViewSnapshot: null,
    nameModalVisible: false,
    pendingName: '',
    editModalVisible: false,
    editingView: null,
    editName: '',
    editReplaceFilters: false,
  }),
  computed: {
    views() {
      return this.$store.getters['monitor-views/list'];
    },
    activeView() {
      if (!this.activeViewId) return null;
      return this.views.find(v => idOf(v) === this.activeViewId) || null;
    },
    triggerLabel() {
      if (this.activeView) return this.activeView.name;
      return this.$t('monitor.views.placeholder');
    },
    isModified() {
      if (!this.activeViewSnapshot) return false;
      return JSON.stringify(this.currentFilters) !== this.activeViewSnapshot;
    },
    editFiltersDiffer() {
      if (!this.editingView) return false;
      return JSON.stringify(this.editingView.filters || {}) !== JSON.stringify(this.currentFilters);
    },
    canCommitEdit() {
      if (!this.editingView) return false;
      const trimmed = (this.editName || '').trim();
      if (!trimmed) return false;
      const nameChanged = trimmed !== this.editingView.name;
      return nameChanged || this.editReplaceFilters;
    },
  },
  async mounted() {
    this.loading = true;
    try {
      await this.$store.dispatch('monitor-views/get');
    } catch (err) {
      this.$notification.error({
        message: this.$t('monitor.views.loadError'),
        description: err?.response?.data?.message || err?.message,
      });
    } finally {
      this.loading = false;
      this.loaded = true;
    }
  },
  methods: {
    idOf,
    loadView(view) {
      const id = idOf(view);
      if (!id) return;
      const payload = view.filters || {};
      const migrated = migrateFilters(payload, view.version || 1);
      // Warn (non-blocking) when the saved version is newer than what this
      // client understands - the frontend will ignore unknown keys, but the
      // user should know the load may be partial.
      if ((view.version || 1) > CURRENT_VERSION) {
        this.$notification.warning({
          message: this.$t('monitor.views.incompatible'),
        });
      }
      this.activeViewId = id;
      this.activeViewSnapshot = JSON.stringify(migrated);
      this.$emit('apply', migrated);
    },
    openSaveModal() {
      this.pendingName = '';
      this.nameModalVisible = true;
    },
    async commitSaveAsNew() {
      const name = this.pendingName.trim();
      if (!name || this.saving) return;
      this.saving = true;
      try {
        const result = await this.$store.dispatch('monitor-views/post', {
          name,
          version: CURRENT_VERSION,
          filters: this.currentFilters,
        });
        const created = result?.data;
        const createdId = idOf(created);
        if (createdId) {
          this.activeViewId = createdId;
          this.activeViewSnapshot = JSON.stringify(created.filters || {});
        }
        this.$notification.success({
          message: this.$t('monitor.views.saved', { name }),
        });
        this.nameModalVisible = false;
      } catch (err) {
        this.$notification.error({
          message: this.$t('monitor.views.saveError'),
          description: err?.response?.data?.message || err?.message,
        });
      } finally {
        this.saving = false;
      }
    },
    async updateActive() {
      if (!this.activeView) return;
      const id = idOf(this.activeView);
      const { name } = this.activeView;
      if (!id) return;
      try {
        await this.$store.dispatch('monitor-views/patch', {
          id,
          body: {
            version: CURRENT_VERSION,
            filters: this.currentFilters,
          },
        });
        this.activeViewSnapshot = JSON.stringify(this.currentFilters);
        this.$notification.success({
          message: this.$t('monitor.views.updated', { name }),
        });
      } catch (err) {
        this.$notification.error({
          message: this.$t('monitor.views.saveError'),
          description: err?.response?.data?.message || err?.message,
        });
      }
    },
    openEditModal(view) {
      this.editingView = view;
      this.editName = view.name;
      this.editReplaceFilters = false;
      this.editModalVisible = true;
    },
    async commitEdit() {
      if (!this.canCommitEdit || this.saving) return;
      const view = this.editingView;
      const viewId = idOf(view);
      if (!viewId) return;
      const trimmedName = this.editName.trim();
      const body = {};
      const nameChanged = trimmedName !== view.name;
      const replacingFilters = this.editReplaceFilters;
      if (nameChanged) body.name = trimmedName;
      if (replacingFilters) {
        body.version = CURRENT_VERSION;
        body.filters = this.currentFilters;
      }
      this.saving = true;
      try {
        await this.$store.dispatch('monitor-views/patch', { id: viewId, body });
        if (this.activeViewId === viewId && replacingFilters) {
          this.activeViewSnapshot = JSON.stringify(this.currentFilters);
        }
        this.$notification.success({
          message: this.$t('monitor.views.updated', { name: trimmedName }),
        });
        this.editModalVisible = false;
      } catch (err) {
        this.$notification.error({
          message: this.$t('monitor.views.saveError'),
          description: err?.response?.data?.message || err?.message,
        });
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(view) {
      this.$confirm({
        title: this.$t('monitor.views.confirmDelete', { name: view.name }),
        okText: this.$t('monitor.views.delete'),
        okType: 'danger',
        onOk: () => this.deleteView(view),
      });
    },
    async deleteView(view) {
      const id = idOf(view);
      if (!id) return;
      try {
        await this.$store.dispatch('monitor-views/delete', id);
        if (this.activeViewId === id) {
          this.activeViewId = null;
          this.activeViewSnapshot = null;
        }
        this.$notification.success({
          message: this.$t('monitor.views.deleted', { name: view.name }),
        });
      } catch (err) {
        this.$notification.error({
          message: this.$t('monitor.views.saveError'),
          description: err?.response?.data?.message || err?.message,
        });
      }
    },
    clearActive() {
      this.activeViewId = null;
      this.activeViewSnapshot = null;
    },
  },
};
</script>

<style scoped>
.msv {
  display: inline-flex;
  align-items: center;
}

.msv__trigger.ant-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.msv__label {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.msv__caret {
  font-size: 10px;
  opacity: 0.6;
}

.msv__modified-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #fbbf24;
  display: inline-block;
}
</style>

<style>
.msv__menu.ant-dropdown-menu {
  min-width: 240px;
  max-height: 320px;
  overflow-y: auto;
}

.msv__empty.ant-dropdown-menu-item {
  color: #9ca3af;
  font-style: italic;
  cursor: default;
}

.msv__item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.msv__item-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.msv__item-icon {
  color: #fbbf24;
}

.msv__item-action {
  color: #9ca3af;
  font-size: 12px;
  padding: 4px;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.15s, background 0.15s, color 0.15s;
}

.msv__menu .ant-dropdown-menu-item:hover .msv__item-action {
  opacity: 1;
}

.msv__item-action:hover {
  background: rgba(59, 130, 246, 0.12);
  color: #3b82f6;
}

.msv__item-action--danger:hover {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.msv__edit-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.msv__edit-label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.msv__edit-hint {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
  font-style: italic;
}

.msv__item--active.ant-dropdown-menu-item {
  background: rgba(251, 191, 36, 0.08);
}
</style>
